import React from 'react';

/**
 * Day 4 — 7-day bowel pattern strip (read-only, deterministic).
 *
 * Renders 7 day cells oldest → newest.
 * Tokens accepted: B1..B7, 'Yes', 'None', '—' (no entry that day).
 * Fails safe: renders nothing when no strip data provided.
 */

const TOKEN_STYLES = {
  // Hard stool pattern — amber (observational, not alarming)
  B1: { bg: 'bg-amber-100', border: 'border-amber-300', text: 'text-amber-800' },
  B2: { bg: 'bg-amber-100', border: 'border-amber-300', text: 'text-amber-800' },
  // Normal range — emerald
  B3: { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-800' },
  B4: { bg: 'bg-emerald-100', border: 'border-emerald-400', text: 'text-emerald-900' },
  B5: { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-800' },
  // Loose stool pattern — sky (observational)
  B6: { bg: 'bg-sky-100', border: 'border-sky-300', text: 'text-sky-800' },
  B7: { bg: 'bg-sky-100', border: 'border-sky-300', text: 'text-sky-800' },
  // Recorded opened but no Bristol type given
  Yes: { bg: 'bg-slate-100', border: 'border-slate-300', text: 'text-slate-700' },
  // Bowel not opened
  None: { bg: 'bg-rose-50', border: 'border-rose-300', text: 'text-rose-700' },
  // No entry at all that day
  '—': { bg: 'bg-muted/40', border: 'border-dashed border-slate-300', text: 'text-muted-foreground' },
};

const DAY_LABEL = (offsetFromToday) => {
  const d = new Date();
  d.setDate(d.getDate() - offsetFromToday);
  return d.toLocaleDateString(undefined, { weekday: 'short' });
};

const TOKEN_TITLE = {
  B1: 'Bristol 1 — separate hard lumps',
  B2: 'Bristol 2 — lumpy and sausage-like',
  B3: 'Bristol 3 — sausage shape with cracks',
  B4: 'Bristol 4 — smooth and soft',
  B5: 'Bristol 5 — soft blobs with clear-cut edges',
  B6: 'Bristol 6 — mushy with ragged edges',
  B7: 'Bristol 7 — entirely liquid',
  Yes: 'Bowel opened — stool type not recorded',
  None: 'No bowel action recorded',
  '—': 'No entry recorded on this day',
};

export function BowelPatternStrip({ strip, windowDays = 7 }) {
  if (!Array.isArray(strip) || strip.length === 0) return null;
  const cells = strip.slice(0, windowDays);
  // strip is oldest → newest; offset-from-today for display label
  const total = cells.length;
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="bowel-pattern-strip">
      {cells.map((tok, idx) => {
        const style = TOKEN_STYLES[tok] || TOKEN_STYLES['—'];
        const offset = total - 1 - idx;
        const label = offset === 0 ? 'Today' : DAY_LABEL(offset);
        return (
          <div
            key={idx}
            title={`${label} — ${TOKEN_TITLE[tok] || tok}`}
            data-testid={`bowel-pattern-cell-${idx}`}
            className={`flex flex-col items-center justify-center w-10 h-12 rounded-md border ${style.bg} ${style.border} ${style.text}`}
          >
            <span className="text-[9px] uppercase tracking-wide opacity-70">{label}</span>
            <span className="text-xs font-semibold leading-none">{tok}</span>
          </div>
        );
      })}
    </div>
  );
}

export default BowelPatternStrip;
